import { useState, useEffect } from 'react'
import { StreamChat } from 'stream-chat'
import { gql, useApolloClient } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import { STREAM_API_KEY } from 'env'
import { useAuth } from 'lib/auth'
import { v1 as uuid } from 'uuid'

const GET_STREAM_ACCESS_TOKEN = gql`
  query StreamAccessToken {
    streamAccessToken
  }
`

const stream = StreamChat.getInstance(STREAM_API_KEY)

const useStreamClient = () => {
  const [streamClient, setStreamClient] = useState(null)
  const { user } = useAuth()
  const apolloClient = useApolloClient()

  const tokenProvider = async () => {
    const { data } = await apolloClient.query({
      query: GET_STREAM_ACCESS_TOKEN,
    })
    return data?.streamAccessToken
  }

  useEffect(() => {
    const connectUser = async () => {
      if (stream.user?.id !== user.externalId) {
        await stream.connectUser({ id: user.externalId }, tokenProvider)
      }
      setStreamClient(stream)
    }

    /* eslint-disable react-hooks/exhaustive-deps */
    const disconnectUser = async () => {
      await stream.disconnectUser()
      setStreamClient(null)
    }

    if (user) {
      connectUser()
    } else {
      disconnectUser()
    }
  }, [user])
  /* eslint-enable react-hooks/exhaustive-deps */

  return streamClient
}

const useFindOrCreateChannelWithUserIds = () => {
  const client = useStreamClient()
  const currentUserId = client?.user?.id

  const findOrCreateChannelWithUserIds = async (userIds, { name } = {}) => {
    if (!currentUserId || userIds.length === 0) return null
    if (userIds.includes(undefined)) return null

    const channelMembers = Array.from(new Set([currentUserId, ...userIds]))

    try {
      const batchSize = 99
      const baseChannelConfig = {
        members: channelMembers.slice(
          0,
          Math.min(batchSize, channelMembers.length),
        ),
      }

      const trimmedName = name?.trim()
      if (trimmedName) {
        baseChannelConfig.name = trimmedName
      }

      // For 1-on-1 conversations (2 members), use distinct channels to prevent duplicates.
      // For group conversations (3+ members), use non-distinct to allow adding members.
      const isDirectMessage = channelMembers.length === 2

      // Distinct channels automatically use member-based IDs, so we only generate
      // an ID for non-distinct channels (groups).
      const channelId = isDirectMessage ? null : uuid()
      baseChannelConfig.distinct = isDirectMessage

      const channel = client.channel('messaging', channelId, baseChannelConfig)
      await channel.watch()

      if (channelMembers.length > batchSize) {
        const tasks = []
        for (let i = batchSize; i < channelMembers.length; i += batchSize) {
          const batch = channelMembers.slice(
            i,
            Math.min(i + batchSize, channelMembers.length),
          )
          tasks.push(channel.addMembers(batch))
        }
        await Promise.all(tasks)
      }

      return channel
    } catch (e) {
      window.rg4js &&
        window.rg4js('send', {
          error: e,
          tags: ['messaging', 'stream'],
        })
      return null
    }
  }
  return findOrCreateChannelWithUserIds
}

const useUnhideChannels = () => {
  const client = useStreamClient()
  const currentUserId = client?.user?.id

  const unhideChannels = async channelIds => {
    if (!currentUserId || channelIds.length === 0) return null
    try {
      for (const id of channelIds) {
        const channel = client.channel('messaging', id)
        await channel.show()
      }
      const lastUpdatedChannelId = channelIds[channelIds.length - 1]
      return client.channel('messaging', lastUpdatedChannelId)
    } catch (e) {
      window.rg4js &&
        window.rg4js('send', {
          error: e,
          tags: ['messaging', 'stream'],
        })
      return null
    }
  }
  return unhideChannels
}

const useNavigateToChannel = () => {
  const auth = useAuth()
  const history = useHistory()

  const navigateToChannel = channel => {
    if (auth.isCoach) {
      history.push(`/pga-coach/messages/${channel.id}`)
    } else if (auth.isConsumer) {
      history.push(`/club/messages/${channel.id}`)
    }
  }
  return navigateToChannel
}

const useUnreadMessagesCount = () => {
  const streamClient = useStreamClient()
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0)

  useEffect(() => {
    if (streamClient) {
      const handleUnreadCountChange = event => {
        setUnreadMessagesCount(event.total_unread_count ?? 0)
      }
      // initial unread count
      setUnreadMessagesCount(streamClient?.user?.total_unread_count ?? 0)

      streamClient.on('notification.message_new', handleUnreadCountChange)
      streamClient.on('notification.mark_read', handleUnreadCountChange)

      return () => {
        streamClient.off('notification.message_new', handleUnreadCountChange)
        streamClient.off('notification.mark_read', handleUnreadCountChange)
      }
    }
  }, [streamClient])

  return unreadMessagesCount
}

export {
  useStreamClient,
  useFindOrCreateChannelWithUserIds,
  useNavigateToChannel,
  useUnhideChannels,
  useUnreadMessagesCount,
}
